
import { useState, useEffect } from 'react';
import { X, FileVideo } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface VideoPreviewProps {
  url: string;
  onRemove: () => void;
  disabled?: boolean;
}

export const VideoPreview = ({ url, onRemove, disabled = false }: VideoPreviewProps) => {
  const [isLinkedInAsset, setIsLinkedInAsset] = useState(false);
  const [localVideoUrl, setLocalVideoUrl] = useState(url);
  
  useEffect(() => {
    // Check if this is a LinkedIn asset URL
    if (url.startsWith('urn:li:digitalmediaAsset:')) {
      setIsLinkedInAsset(true);
      // For LinkedIn assets, we don't have a direct URL to display
      // We'll show a placeholder instead
      setLocalVideoUrl('');
    } else {
      setIsLinkedInAsset(false);
      setLocalVideoUrl(url);
    }
  }, [url]);
  
  return (
    <div className="relative border rounded-md overflow-hidden">
      <div className="aspect-video bg-gray-100">
        {isLinkedInAsset ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-primary">
              <FileVideo className="mx-auto h-12 w-12" />
            </div>
            <p className="mt-2 text-sm text-muted-foreground">LinkedIn Video</p>
            <p className="text-xs text-muted-foreground">Will be displayed in your LinkedIn post</p>
          </div>
        ) : (
          <video 
            src={localVideoUrl} 
            controls 
            className="w-full h-full object-contain"
            onError={(e) => {
              // Handle video load error
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              const container = target.parentElement;
              if (container) {
                container.classList.add('flex', 'items-center', 'justify-center');
                const fallback = document.createElement('div');
                fallback.className = 'flex flex-col items-center justify-center h-full';
                fallback.innerHTML = `
                  <div class="text-gray-400">
                    <svg class="mx-auto h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p class="mt-2 text-sm text-gray-500">Video preview not available</p>
                `;
                container.appendChild(fallback);
              }
            }}
          />
        )}
      </div>
      <Button
        variant="destructive"
        size="icon"
        className="absolute top-2 right-2"
        onClick={onRemove}
        disabled={disabled}
        type="button"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
